/**
 * Solex Document Editor Window Management Utilities
 *
 * Provides reusable utilities for opening and managing Solex editor windows.
 * Extracts duplicate window opening logic from OrderButton.tsx (EN-3255).
 */

/**
 * HTML template displayed while loading Solex Document Editor
 * Used to show loading spinner in placeholder window
 */
export const SOLEX_LOADING_HTML = `
<html>
  <body style="font-family: Arial, sans-serif; padding: 40px; text-align: center;">
    <h2>Loading Solex Document Editor...</h2>
    <p>Please wait while we prepare your document editing session.</p>
    <div style="margin-top: 30px;">
      <div style="border: 4px solid #f3f3f3; border-top: 4px solid #3498db;
                  border-radius: 50%; width: 40px; height: 40px;
                  animation: spin 1s linear infinite; display: inline-block;">
      </div>
    </div>
    <style>
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    </style>
  </body>
</html>
`;

/**
 * Opens a placeholder window with loading screen
 *
 * This must be called synchronously during a user gesture (e.g., button click)
 * to prevent popup blockers. The window is opened immediately, then the loading
 * HTML is written to it while async operations complete.
 *
 * @returns Window object if successful, null if popup was blocked
 */
export const openPlaceholderWindow = (): Window | null => {
  try {
    const newWindow = window.open('about:blank', '_blank', 'noopener,noreferrer');
    if (newWindow) {
      // Ensure the new window cannot reference or navigate the opener (reverse-tabnabbing protection)
      newWindow.opener = null;
      newWindow.document.write(SOLEX_LOADING_HTML);
      newWindow.document.close();
      return newWindow;
    }
    return null;
  } catch (error) {
    console.error('[Solex] Failed to open window:', error);
    return null;
  }
};

/**
 * Safely closes a window reference
 *
 * Handles cleanup with proper error handling and null checks.
 * Safe to call on already-closed or null windows.
 *
 * @param windowRef Window to close
 */
export const cleanupWindow = (windowRef: Window | null): void => {
  if (windowRef && !windowRef.closed) {
    try {
      windowRef.close();
    } catch (error) {
      console.error('[Solex] Failed to close window:', error);
    }
  }
};
